import api_base from '@/api/base';

/* =========================
   Types
========================= */

export type CRMContactStatus =
  | 'new'
  | 'contacted'
  | 'interested'
  | 'follow_up'
  | 'meeting'
  | 'negotiation'
  | 'won'
  | 'lost';
  
export interface CRMTag {
  id: string;
  name: string;
}

export interface CRMTagWrite {
  name: string;
}

export interface CRMContact {
  id: string;
  
  name: string;
  phone?: string | null;
  email?: string | null;
  linkedin?: string | null;
  instagram?: string | null;

  status: CRMContactStatus;

  // backend returns tag IDs (serializer uses PrimaryKeyRelatedField)
  tags?: string[];

  created_at: string;
  updated_at: string;
}

export interface CRMContactWrite {
  name: string;
  phone?: string | null;
  email?: string | null;
  linkedin?: string | null;
  instagram?: string | null;
  status?: CRMContactStatus;
  tags?: string[]; // tag IDs
}

export interface CRMBulkUpsertRequest {
  contacts: CRMContactWrite[];
}

export interface CRMBulkUpsertResponse {
  ids: string[];
  count: number;
}

export interface CRMImportResponse {
  processed: number;
  saved_or_merged: number;
  skipped_invalid: number;
  created_tags: number;
  errors: Array<{
    row: number;
    errors: Record<string, any>;
  }>;
}

/* =========================
   TAGS
   Django:
   - CRMTagListCreateView
   - CRMTagDetailView
========================= */

/**
 * List tags
 * GET /api/v1/crm/tags/
 */
export const list_crm_tags = async (): Promise<CRMTag[]> => {
  const res = await api_base.get('/api/v1/crm/tags/');
  return res.data.results ?? res.data;
};

/**
 * Create tag
 * POST /api/v1/crm/tags/
 */
export const create_crm_tag = async (data: CRMTagWrite): Promise<CRMTag> => {
  const res = await api_base.post('/api/v1/crm/tags/', data);
  return res.data;
};

/**
 * Get tag by UUID
 * GET /api/v1/crm/tags/<uuid>/
 */
export const get_crm_tag = async (id: string): Promise<CRMTag> => {
  const res = await api_base.get(`/api/v1/crm/tags/${id}/`);
  return res.data;
};

/**
 * Update tag (PATCH)
 * PATCH /api/v1/crm/tags/<uuid>/
 */
export const update_crm_tag = async (id: string, data: Partial<CRMTagWrite>): Promise<CRMTag> => {
  const res = await api_base.patch(`/api/v1/crm/tags/${id}/`, data);
  return res.data;
};

/**
 * Delete tag
 * DELETE /api/v1/crm/tags/<uuid>/
 */
export const delete_crm_tag = async (id: string): Promise<void> => {
  await api_base.delete(`/api/v1/crm/tags/${id}/`);
};

/* =========================
   CONTACTS
   Django:
   - CRMContactListCreateView
   - CRMContactDetailView
========================= */

/**
 * List contacts
 * GET /api/v1/crm/contacts/
 */
export const list_crm_contacts = async (): Promise<CRMContact[]> => {
  const res = await api_base.get('/api/v1/crm/contacts/');
  return res.data.results ?? res.data;
};

/**
 * Create contact
 * POST /api/v1/crm/contacts/
 */
export const create_crm_contact = async (data: CRMContactWrite): Promise<CRMContact> => {
  const res = await api_base.post('/api/v1/crm/contacts/', data);
  return res.data;
};

/**
 * Get contact by UUID
 * GET /api/v1/crm/contacts/<uuid>/
 */
export const get_crm_contact = async (id: string): Promise<CRMContact> => {
  const res = await api_base.get(`/api/v1/crm/contacts/${id}/`);
  return res.data;
};

/**
 * Update contact (PATCH)
 * PATCH /api/v1/crm/contacts/<uuid>/
 */
export const update_crm_contact = async (
  id: string,
  data: Partial<CRMContactWrite>
): Promise<CRMContact> => {
  const res = await api_base.patch(`/api/v1/crm/contacts/${id}/`, data);
  return res.data;
};

/**
 * Delete contact
 * DELETE /api/v1/crm/contacts/<uuid>/
 */
export const delete_crm_contact = async (id: string): Promise<void> => {
  await api_base.delete(`/api/v1/crm/contacts/${id}/`);
};

/* =========================
   BULK UPSERT
   Django:
   - CRMContactBulkUpsertView
========================= */

/**
 * Bulk upsert contacts
 * POST /api/v1/crm/contacts/bulk/
 */
export const bulk_upsert_crm_contacts = async (
  data: CRMBulkUpsertRequest
): Promise<CRMBulkUpsertResponse> => {
  const res = await api_base.post('/api/v1/crm/contacts/bulk/', data);
  return res.data;
};

/* =========================
   IMPORT CSV/XLSX
   Django:
   - CRMContactImportView
========================= */

export interface CRMImportOptions {
  create_missing_tags?: boolean; // default false
  tag_separator?: string;        // default ","
}

/**
 * Import contacts from CSV/XLSX
 * POST /api/v1/crm/contacts/import/
 *
 * Backend expects multipart/form-data:
 * - file (required)
 * - create_missing_tags (optional)
 * - tag_separator (optional)
 */
export const import_crm_contacts = async (
  file: File,
  opts: CRMImportOptions = {}
): Promise<CRMImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  if (opts.create_missing_tags !== undefined) {
    formData.append('create_missing_tags', String(opts.create_missing_tags));
  }
  if (opts.tag_separator) {
    formData.append('tag_separator', opts.tag_separator);
  }

  const res = await api_base.post('/api/v1/crm/contacts/import/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return res.data;
};